import { TextColors } from '@picsart/design-system/foundation/colors';

/**
 * Shared values for the header's auth cluster, so the Pricing pill, the Projects
 * pill, the avatar and the menu agree without importing each other's styles.
 *
 * The literal colors here are deliberate. `@picsart/design-system` foundation
 * colors are theme-aware surface/text scales; they carry no brand-magenta and no
 * gradient tokens (`TextColors` is only base/tint1-3/tooltip). These three
 * values are the product's own accent, matching the accent already used across
 * gen.ai — the same reason `HeaderAuthSection` hardcodes the coins gold.
 */

/** gen.ai's valentine-pink accent — promo badge, premium marks, Upgrade pill. */
export const ACCENT = '#E859B4';

/** The avatar ring, and the accent fills in the user menu. */
export const ACCENT_GRADIENT = 'linear-gradient(93deg, #FF7A45 0%, #E859B4 55%, #C13BE8 100%)';

/**
 * The promo badge's fill — the same three stops as `ACCENT_GRADIENT`, running
 * the other way.
 *
 * Violet leads and warm gold lands on the right, which is how the badge is
 * drawn in the design. It sits under the left edge of the Pricing pill, far
 * enough from the avatar ring's gradient that the two are never adjacent and
 * read as one palette rather than a mismatch.
 */
export const PROMO_GRADIENT = 'linear-gradient(93deg, #C13BE8 0%, #E859B4 55%, #FF9F45 100%)';

/** Foreground on the accent fills above; both are dark enough for white. */
export const ON_ACCENT = '#FFFFFF';

/** Every pill in the header cluster shares one height so they align optically. */
export const HEADER_CONTROL_HEIGHT = 36;

/**
 * Pill corners on the header controls use `CornerRadius.r20` — never
 * `CornerRadius.circle`.
 *
 * `circle` is `50%`, a percentage of *each* axis: on a square (an avatar) that
 * draws a circle, but on anything wider than it is tall it draws an **ellipse**,
 * which is what turned the Pricing/Projects/Sign In buttons into ovals.
 *
 * A length-valued radius is what gives a pill, and it does not need to be exact:
 * when the radii on one side exceed the box, CSS scales them down proportionally,
 * so any token at or above half the control height renders as a true pill.
 * `r20` (20px) is the smallest DS step above half of `HEADER_CONTROL_HEIGHT`
 * (18px), so it clamps to a pill at any width. Shorter controls take the
 * equivalent step for their own height — `r16` on the 28px Upgrade pill, `r8` on
 * the promo badge.
 */

/**
 * Every DS icon needs this, or it renders as nothing.
 *
 * `createSVG` (the factory behind `@picsart/design-system/foundation/icons/*`)
 * hardcodes `fill="none"` on the `<svg>` and its paths carry no `fill` of their
 * own, so they inherit `none` and paint nothing — the element is present and
 * correctly sized, just invisible. Setting `color` alone does nothing; the paint
 * has to be routed to `fill`.
 *
 * `currentColor` keeps `color` as the knob, so each icon class can still set its
 * own colour. Safe for the whole icon set: none of them use `stroke` — even the
 * `*Outline` icons draw their outlines as filled geometry.
 */
export const iconPaint = { fill: 'currentColor' } as const;

/**
 * Consistent keyboard focus treatment for the hand-rolled buttons.
 *
 * Deliberately no `border-radius`: an outline already follows the radius the
 * element itself has, so the ring matches whatever it is drawn around without
 * being told. Setting one here instead reshaped the element on focus, which is
 * why every consumer carried a `borderRadius` beside this spread purely to undo
 * it — and where two such rules met on one element (a `/profile` run tile is a
 * `MediaCarousel`, so it takes both the grid's tile class and the carousel's
 * own) the winner came down to stylesheet order, and the white ring was drawn
 * to a different radius than the card underneath it.
 */
export const focusRing = {
  '&:focus-visible': {
    outline: `2px solid ${TextColors.base}`,
    outlineOffset: 2,
  },
} as const;
